import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Send, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

interface Parent {
  parent_id: string | null;
  parent_name: string | null;
}

interface Props {
  parents: Parent[];
  nurseryId: string;
  onSuccess: () => void;
}

export default function SendMessageDialog({ parents, nurseryId, onSuccess }: Props) {
  const [open, setOpen] = useState(false);
  const { session } = useAuth();
  const { toast } = useToast();
  const [receiverId, setReceiverId] = useState<string>("all");
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);

  const uniqueParents = parents.filter((p, i, arr) => p.parent_id && arr.findIndex((x) => x.parent_id === p.parent_id) === i);

  const handleSend = async () => {
    if (!content.trim() || !session?.user?.id) return;
    setSending(true);

    const targets = receiverId === "all"
      ? uniqueParents.filter((p) => p.parent_id).map((p) => p.parent_id!)
      : [receiverId];

    const messages = targets.map((rid) => ({
      content: content.trim(),
      sender_id: session.user.id,
      receiver_id: rid,
      nursery_id: nurseryId,
    }));

    if (messages.length === 0) {
      toast({ title: "لا يوجد أولياء أمور لإرسال الرسالة إليهم", variant: "destructive" });
      setSending(false);
      return;
    }

    const { error } = await supabase.from("messages").insert(messages);
    if (error) {
      toast({ title: "خطأ", description: error.message, variant: "destructive" });
    } else {
      toast({ title: `تم إرسال الرسالة إلى ${messages.length} ولي أمر` });
      setContent("");
      setReceiverId("all");
      setOpen(false);
      onSuccess();
    }
    setSending(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gradient-hero border-0">
          <Send className="w-4 h-4 ml-2" />
          إرسال رسالة
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" dir="rtl">
        <DialogHeader>
          <DialogTitle>إرسال رسالة / إشعار</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>إرسال إلى</Label>
            <Select value={receiverId} onValueChange={setReceiverId}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع أولياء الأمور</SelectItem>
                {uniqueParents.map((p) => (
                  <SelectItem key={p.parent_id!} value={p.parent_id!}>{p.parent_name || "بدون اسم"}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>نص الرسالة *</Label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="اكتب رسالتك هنا..."
              rows={4}
              maxLength={1000}
            />
            <p className="text-xs text-muted-foreground mt-1 text-left">{content.length}/1000</p>
          </div>
          <Button onClick={handleSend} className="w-full" disabled={sending || !content.trim()}>
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "إرسال"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
